import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import PageBase from "../../../components/PageBase";
import Field from "../../../components/FieldGroup/Field";
import bullet from '../../../assets/title-bullet.png';
import { formatDate } from "../../../utils/general";
import moment from "moment";
import constants from "../../../utils/constants";
import * as ROUTES from "../../../utils/routes";
import FieldGroup from "../../../components/FieldGroup/index";
import ObjectSummaryRow from "../../../components/ObjectSummaryRow";
import { coSellOpportunityFields, getLabelByField } from '../../../utils/fields/coSellOpportunities';
import { coSellOpportunitySections } from '../../../utils/fields/coSellOpportunitiesSections';

import './ViewCoSellOpportunity.scss';

const ViewCoSellOpportunity = () => {
	const params = useParams();
	const coSellOpportunities = useSelector(state => state.coSellOpportunity.coSellOpportunities);

	// Find opportunity from the list based on ID
	const opportunity = coSellOpportunities?.find(op => op?.Id === params.id);

	// Parse error details from Last_Sync_Status__c
	let errorDetails = [];
	let errorSummary = '';
	if (opportunity?.Last_Sync_Status__c && opportunity.Last_Sync_Status__c.startsWith('Failed') && opportunity.Last_Sync_Status__c.includes('{')) {
		try {
			const jsonStart = opportunity.Last_Sync_Status__c.indexOf('{');
			const summaryText = opportunity.Last_Sync_Status__c.substring(7, jsonStart).trim(); // text sau 'Failed: '
			if (summaryText.includes('CreateOpportunity failed')) {
				errorSummary = 'Create Co-sell opportunity failed';
			} else if (summaryText.includes('UpdateOpportunity failed')) {
				errorSummary = 'Update Co-sell opportunity failed';
			} else if (summaryText) {
				errorSummary = summaryText;
			} else {
				errorSummary = 'Sync operation failed';
			}
			const jsonString = opportunity.Last_Sync_Status__c.substring(jsonStart);
			const errorObj = JSON.parse(jsonString);
			if (errorObj.ErrorList && Array.isArray(errorObj.ErrorList)) {
				errorDetails = errorObj.ErrorList.map(error => ({
					code: error.Code || 'N/A',
					fieldName: error.FieldName || 'N/A',
					message: error.Message || 'N/A'
				}));
			}
		} catch (e) {
			errorSummary = 'Sync operation failed';
		}
	} else if (opportunity?.Last_Sync_Status__c && opportunity.Last_Sync_Status__c.startsWith('Failed:')) {
		// No JSON, only summary
		errorSummary = opportunity.Last_Sync_Status__c.substring(7).trim() || 'Sync operation failed';
	}

	if (opportunity == null) {
		return (
			<PageBase title="Co-Sell Opportunity"
				removeMargins
				bodyBg="#fff"
				headerLinkText="All Co-Sell Opportunities"
				headerLinkTarget={ROUTES.CO_SELL_OPPORTUNITIES}
			>
				<div style={{ padding: '20px', textAlign: 'center' }}>
					<p>Co-Sell Opportunity not found. Please go back to the list and try again.</p>
					<Link to={ROUTES.CO_SELL_OPPORTUNITIES}>← Back to Co-Sell Opportunities</Link>
				</div>
			</PageBase>
		);
	}

	const summary = [
		{ title: 'Opportunity Name', value: opportunity.Name, blue: true },
		{ title: 'End Customer', value: opportunity.End_Customer__r?.Name || 'N/A' },
		{ title: 'ACE Stage', value: opportunity.ACE_Stage__c || 'N/A' },
		{ title: 'Amount', value: opportunity.Amount__c ? `$${opportunity.Amount__c.toLocaleString()}` : 'N/A' },
		{ title: 'Close Date', value: opportunity.CloseDate__c ? formatDate(opportunity.CloseDate__c) : 'N/A' },
		{ title: 'Priority', value: opportunity.Priority__c || 'N/A' },
	];

	const getFieldValue = (obj, fieldPath) => {
		const path = fieldPath.split('.');
		let value = obj;
		for (let p of path) value = value ? value[p] : undefined;
		return value;
	};

	const formatFieldValue = (field, value) => {
		if (field.toLowerCase().includes('date') && value) {
			return formatDate(value);
		}
		if (field.toLowerCase().includes('amount') && value) {
			return `$${Number(value).toLocaleString()}`;
		}
		if (typeof value === 'boolean') {
			return (
				<input
					type="checkbox"
					checked={value}
					disabled
					style={{ width: 18, height: 18 }}
				/>
			);
		}
		return value;
	};

	return (
		<PageBase title="Co-Sell Opportunity"
			removeMargins
			bodyBg="#fff"
			headerLinkText="All Co-Sell Opportunities"
			headerLinkTarget={ROUTES.CO_SELL_OPPORTUNITIES}
		>
			<div className="view-co-sell-opportunity-title">
				<img src={bullet} style={{ width: '24px', marginRight: '20px' }} />
				<span style={{ whiteSpace: 'nowrap' }}>{opportunity.Name}</span>
			</div>

			<ObjectSummaryRow data={summary} />

			<div style={{ height: '100%', overflowY: 'auto', padding: '0 20px' }}>

				{/* Sync Status Display */}
				<div className="sync-status-container" style={{ margin: '20px 20px 0 20px' }}>
					<div className="sync-info-row">
						<span className="info-label">Last Sync Date:</span>
						<span className="info-value">
							{opportunity.Last_Sync_Date__c ? moment(opportunity.Last_Sync_Date__c).format(constants.DATETIME_FOTMAT) : 'No data available'}
						</span>
					</div>
					<div className="sync-info-row">
						<span className="info-label">Last Sync Status:</span>
						{(() => {
							let status = 'Unknown';
							if (opportunity.Last_Sync_Status__c === 'Success') status = 'Success';
							else if (opportunity.Last_Sync_Status__c && opportunity.Last_Sync_Status__c.startsWith('Failed:')) status = 'Failed';
							return (
								<span className={
									status === 'Success' ? 'success-status' : status === 'Failed' ? 'failed-status' : ''
								}>
									{status}
								</span>
							);
						})()}
					</div>
					{opportunity.Last_Sync_Status__c && opportunity.Last_Sync_Status__c.startsWith('Failed') && errorSummary && (
						<div className="sync-info-row error-summary">
							<span className="info-label">Error Summary:</span>
							<span className="info-value error-text">{errorSummary}</span>
						</div>
					)}
					{errorDetails.length > 0 && (
						<div className="error-details-section">
							<div className="error-details-title">Error Detail:</div>
							<table className="error-table">
								<thead>
									<tr>
										<th>Code</th>
										<th>Field Name</th>
										<th>Message</th>
									</tr>
								</thead>
								<tbody>
									{errorDetails.map((err, idx) => (
										<tr key={idx}>
											<td className="error-code">{err.code}</td>
											<td className="error-field">{err.fieldName}</td>
											<td className="error-message">{err.message}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{coSellOpportunitySections.map(section => (
					<FieldGroup
						key={section.title}
						title={section.title}
						expandable
						expanded
						isCustomGrid={true}
					>
						<div className="section-fields-2col">
							{section.fields.map(f => {
								let value = getFieldValue(opportunity, f.field);
								value = formatFieldValue(f.field, value);
								if (f.field === "Last_Sync_Status__c" && value === "Success") {
									value = <span style={{ color: 'green' }}>Success</span>;
								}
								if (f.field === "Solutions_offered__c") {
									value = [value, getFieldValue(opportunity, "Other_solution_description__c")].join(', ');
								}
								if (value === undefined || value === null || value === '') value = '';
								return <Field key={f.field} title={getLabelByField(f.field)}>
									{f.field === "ACE_Sync_Collector_Prettier__c" && value
										? <div dangerouslySetInnerHTML={{ __html: value }} />
										: value
									}
								</Field>
							})}
						</div>
					</FieldGroup>
				))}
			</div>
		</PageBase>
	)
};

export default ViewCoSellOpportunity; 